import { httpClient, TokenManager, API_BASE_URL, LONG_RUNNING_TIMEOUT } from './httpClient';

export interface ExpertCredentials {
  fullName: string | null;
  title: string | null;
  postNominals: string | null;
  registrationBody: string | null;
  registrationNumber: string | null;
  currentPosition: string | null;
  employer: string | null;
  specialisations: string[];
  yearsOfExperience: number | null;
  qualificationsSummary: string | null;
  expertWitnessStatement: string | null;
  professionalMemberships: string[];
  notableAppointments: string[];
  publicationsSummary: string | null;

  // Solicitor letterhead fields — persisted into the same JSONB column.
  // Required for solicitor profiles, ignored for experts.
  firmName?: string | null;
  firmAddress?: string | null;
  email?: string | null;
  phone?: string | null;
  jobTitle?: string | null;
  sraNumber?: string | null;
  companyNumber?: string | null;
}

export interface SolicitorCredentials {
  firmName: string | null;
  firmAddress: string | null;
  email: string | null;
  phone: string | null;
  fullName: string | null;
  jobTitle: string | null;
  sraNumber: string | null;
  companyNumber: string | null;
}

export const EMPTY_SOLICITOR_CREDENTIALS: SolicitorCredentials = {
  firmName: null,
  firmAddress: null,
  email: null,
  phone: null,
  fullName: null,
  jobTitle: null,
  sraNumber: null,
  companyNumber: null,
};

export interface ExpertProfileResponse {
  displayName: string | null;
  registrationNumber: string | null;
  cvLastParsedAt: string | null;
  credentials: ExpertCredentials;
}

export const EMPTY_CREDENTIALS: ExpertCredentials = {
  fullName: null,
  title: null,
  postNominals: null,
  registrationBody: null,
  registrationNumber: null,
  currentPosition: null,
  employer: null,
  specialisations: [],
  yearsOfExperience: null,
  qualificationsSummary: null,
  expertWitnessStatement: null,
  professionalMemberships: [],
  notableAppointments: [],
  publicationsSummary: null,
};

export const expertProfileApi = {
  async get(): Promise<ExpertProfileResponse> {
    return await httpClient.get<ExpertProfileResponse>('/reports/expert-profile');
  },

  async save(credentials: ExpertCredentials): Promise<{ message: string; displayName: string }> {
    return await httpClient.put<{ message: string; displayName: string }>(
      '/reports/expert-profile',
      { credentials }
    );
  },

  async delete(): Promise<{ message: string }> {
    return await httpClient.delete<{ message: string }>('/reports/expert-profile');
  },

  async parseCv(file: File): Promise<{ credentials: ExpertCredentials; message: string }> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE_URL}/reports/expert-profile/parse-cv`);

      const token = TokenManager.getToken();
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      xhr.timeout = LONG_RUNNING_TIMEOUT;

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            reject(new Error('Invalid response from server'));
          }
        } else {
          try {
            const err = JSON.parse(xhr.responseText);
            reject(new Error(err.message || err.error || `Upload failed (${xhr.status})`));
          } catch {
            reject(new Error(`Upload failed (${xhr.status})`));
          }
        }
      };

      xhr.onerror = () => reject(new Error('Network error during CV upload'));
      xhr.ontimeout = () => reject(new Error('CV parsing timed out'));

      xhr.send(formData);
    });
  },
};
